<template>
  <lf-card :has-padding="hasPadding" :header-divider="false" class="-mb-0 py-6">
    <Loader :is-loading="commercialDataServicesLoading" />
    <div
      class="flex items-center justify-between pb-4"
      :class="{ 'mb-2': !hasBorder }"
    >
      <div
        :class="[{ 'px-6': !hasPadding }]"
        class="flex grow cursor-pointer items-stretch space-x-2"
        @click="handleExpand"
      >
        <slot name="icon">
          <div
            v-if="img || icon"
            class="flex h-6 w-6 items-center justify-center"
          >
            <img v-if="img" :src="img" loading="lazy" />
            <icon-base v-else-if="icon" :icon="icon" view-box="0 0 24 24" />
          </div>
        </slot>
        <lf-h3>
          {{ title }}
        </lf-h3>
        <slot name="dataSelector" />
        <div
          v-if="subtitle"
          class="flex items-stretch space-x-2 align-middle text-label uppercase"
        >
          <span class="text-gray-300">•</span>
          <span class="flex self-center text-xxs font-semibold">
            {{ subtitle }}
          </span>
          <template v-if="date">
            <span class="text-gray-300">•</span>
            <span
              v-tooltip="formatUTCRelativeTime(date)"
              class="flex self-center text-xxs font-semibold"
            >
              {{ formatUTCRelativeTime(date, true) }}
            </span>
          </template>
        </div>
        <slot name="advanced" />
        <div
          v-show="isSuccess"
          v-tooltip="$t('DEALS.SERVICE_RUN_SUCCESSFULLY')"
        >
          <icon-base
            :icon="IconSuccessRoundBig"
            :icon-color="`rgb(${COLORS[0]})`"
          />
        </div>
        <div v-show="isError" v-tooltip="$t('COMMON.ERROR')">
          <icon-base :icon="IconXCircle" :icon-color="`rgb(${COLORS[2]})`" />
        </div>
      </div>
      <slot />
    </div>
    <hr v-if="hasBorder" class="-mx-6 mb-4" />
    <slot v-if="show" name="before-content" />
    <slot v-if="show && !isJsonTreeShown(providerName)" name="content" />
    <slot v-if="show" name="error" />
    <template
      v-if="show && isJsonTreeShown(providerName) && !isEmpty(jsonData)"
    >
      <json-viewer-wrapper
        class="json-viewer-wrapper"
        :data="jsonData"
        :provider-name="providerName"
        :service-name="serviceName"
        :service-type="serviceType"
        :update-service-json-payload="updateServiceJsonPayload"
      />
    </template>
  </lf-card>
</template>

<script setup lang="ts">
import { defineAsyncComponent, ref } from "vue";
import { useStore } from "vuex";

import isEmpty from "lodash/isEmpty";
import { storeToRefs } from "pinia";

import { COLORS } from "@/helpers/constants";
import { formatUTCRelativeTime } from "@/helpers/deals";
import { useServiceDataExpanding, useServices } from "@/hooks/services";
import { useJsonTreeStateStore } from "@/stores/serviceJsonViewer";

import type { UpdateServiceJsonPayload } from "@/models/applications";
import type { JsonViewerData } from "@/models/jsonViewer";
import type { AvailableServices } from "@/models/options";
import type { PropType } from "vue";

const IconSuccessRoundBig = defineAsyncComponent(
  () => import("@/components/icons/IconSuccessRoundBig.vue")
);
const IconXCircle = defineAsyncComponent(
  () => import("@/components/icons/IconXCircle.vue")
);

const JsonViewerWrapper = defineAsyncComponent(
  () =>
    import("@/components/deals/dataServices/jsonViewer/JsonViewerWrapper.vue")
);

const props = defineProps({
  img: {
    type: String,
    default: "",
  },
  hasPadding: {
    type: Boolean,
    default: true,
  },
  icon: {
    type: String,
    default: "",
  },
  title: {
    type: String,
    required: true,
  },
  subtitle: {
    type: String,
    default: "",
  },
  hasBorder: {
    type: Boolean,
    default: true,
  },
  disableShow: {
    type: Boolean,
    default: true,
  },
  isSuccess: {
    type: Boolean,
    default: false,
  },
  isError: {
    type: Boolean,
    default: false,
  },
  date: {
    type: String,
    default: "",
  },
  jsonData: {
    type: Object as PropType<JsonViewerData | null>,
    required: false,
    default: null,
  },
  providerName: {
    type: String,
    required: false,
    default: "",
  },
  serviceName: {
    type: String,
    required: false,
    default: "",
  },
  serviceType: {
    type: String,
    required: false,
    default: "",
  },
  updateServiceJsonPayload: {
    type: Object as PropType<Omit<UpdateServiceJsonPayload, "content">>,
  },
});

const emit = defineEmits<{
  "service:toggle-view": [show: boolean];
}>();

const { commit } = useStore();
const { commercialDataServicesLoading } = useServices();
const { serviceDataExpand } = useServiceDataExpanding(
  props.title as AvailableServices
);

const show = ref(props.disableShow);

const { isJsonTreeShown } = storeToRefs(useJsonTreeStateStore());

const handleExpand = () => {
  show.value = !show.value;
  emit("service:toggle-view", show.value);

  if (show.value) {
    commit("options/setExpandedContent", {
      service: props.title,
      baseDataExpanded: !serviceDataExpand.value.baseDataExpanded,
      additionalDataExpanded: false,
    });
  }
};
</script>
<style scoped>
@reference "@/assets/tailwind.css";

.service-wrapper .json-viewer-wrapper {
  @apply p-6;
}
</style>
